import WebUploader from "webuploader";
import "webuploader/dist/webuploader.css";

import Evented from '@/utils/Evented.js';
import { Store } from "@/utils/store";
import logServeCof from '@/plugins/logServeCof.js';
import Message from '@/utils/resetMessage';
import { getToken } from "@/utils/generalMethods";

import { createRvtUvUploadService } from "@/service/interface";

const BLOB_SIZE = 5 * 1024 * 1024;

class RvtUvUploader extends Evented {
    constructor(schemeType) {
        super();
        this.schemeType = schemeType;
        this.dataSetId = "";

        this.uploadFiles = [];          // 等待上传的文件
        this.uploadingFiles = [];       // 上传中的文件
        this.pausedFiles = [];          // 上传暂停的文件

        this.currFileInfo = null;

        this.onFilesQueued = this.onFilesQueued.bind(this);
        this.onUploadBeforeSend = this.onUploadBeforeSend.bind(this);
        this.onUploadSuccess = this.onUploadSuccess.bind(this);
        this.onUploadError = this.onUploadError.bind(this);

        this.initUploader();
        this.addUploaderEventsListener();
    }

    initUploader() {
        const { uploaders } = Store;
        const find = uploaders.find(el => el.schemeType === this.schemeType);
        if (!find) {
            this.uploader = this.createUploader();
            uploaders.push({ schemeType: this.schemeType, uploader: this });
        }
    }

    createUploader() {
        let webUploader = WebUploader.create({
            // swf文件路径
            swf: "../../node_modules/webuploader/dist/Uploader.swf",

            // 文件接收服务端。
            server: `${window.serverUrl}/${logServeCof.logServeCof()}/project/modelFile/v3/rvtuv/append`,
            timeout: window.uploadTimeout,

            accept: {
                title: "文件上传",
                extensions: "*",
                mimeTypes: ".jpg,.jpeg,.png,.tif,.tiff,.bmp,.exr",
            },
            compress: false,
            chunked: true, // 分片处理大文件
            chunkSize: BLOB_SIZE,
            fileVal: "FileInfo",
            threads: 1,
            chunkRetry: 0,
            duplicate: true,

            auto: false,
        });
        return webUploader;
    }

    addUploaderEventsListener() {
        this.uploader.on("filesQueued", this.onFilesQueued);
        this.uploader.on("uploadBeforeSend", this.onUploadBeforeSend);
        this.uploader.on("uploadSuccess", this.onUploadSuccess);
        this.uploader.on("uploadError", this.onUploadError);
    }

    onFilesQueued(files) {
        files.forEach(file => {
            file.fileName = file.name;
            file.length = file.size;
            file.fileSize = file.size;
            file.schemeType = this.schemeType;

            let fileInfo = {
                id: WebUploader.Base.guid(),
                schemeType: this.schemeType,
                dataSetId: this.dataSetId,
                // 文件状态，前端自定义(waiting: 等待上传, created: 已创建上传索引, paused: 已暂停, continue: 断点续传, update: 文件更新)
                customStatus: this.status,

                fileName: file.name,
                fileSize: file.size,
                length: file.size,
                rate: 0,

                // 文件对象
                file,
            }

            if (this.status === 'update') {
                if (this.currFileInfo.fileName !== fileInfo.fileName) {
                    Message.warning("文件名不匹配，请重试");
                    return;
                }
            }

            this.uploadFiles.push(fileInfo);
        })

        if (this.uploadFiles.length) {
            this.startUploadFiles();
        }

    }

    onUploadBeforeSend(object, data, headers) {
        headers["Authorization"] = getToken();

        data["Index"] = data.chunk ? data.chunk : 0;
    }

    onUploadSuccess(file) {
        this.fire('rvtUvUploadSuccess', {});

        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    onUploadError(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    startUploadFiles() {
        this.fire('rvtUvStartUploadFile', {});

        if (this.uploadingFiles.length) {
            Message.warning("当前有文件正在上传，请耐心等待");
            return;
        }

        let uploadingFile = this.uploadFiles.shift();

        if (uploadingFile.customStatus === 'waiting') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }

        if (uploadingFile.customStatus === 'update') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }
    }

    createFileUpLoad(fileInfo) {
        return new Promise(resolve => {
            let count = fileInfo.fileSize / (BLOB_SIZE);
            let params = {
                fileName: fileInfo.fileName,
                fileSize: fileInfo.fileSize,
                blobCount: Math.ceil(count),
                blobSize: BLOB_SIZE,
                isOverWrite: true
            };

            let headers = {
                Authorization: getToken()
            }
            params.dataSetId = fileInfo.dataSetId;
            createRvtUvUploadService(params, { headers }).then(response => {
                if (response.isSuccess) {
                    let result = response.data;

                    this.uploader.options.formData["rvtUvMappingId"] = result.rvtUvMappingId;
                    this.uploader.options.formData["FileDataId"] = result.fileDataId;
                    this.uploader.options.formData["Key"] = result.fileKey;

                    fileInfo["rvtUvMappingId"] = result.rvtUvMappingId;
                    fileInfo["fileDataId"] = result.fileDataId;
                    fileInfo["Key"] = result.fileKey;

                    resolve(result);
                } else {
                    Message.error(response.errMsg);
                    this.handleFileCreateFail(fileInfo);
                }
            })
        })
    }

    handleUploadSuccess(uploadingFile) {
        if (uploadingFile.customStatus === 'waiting') {
            this.createFileUpLoad(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);
            });
        }
    }

    handleFileCreateFail(fileInfo) {
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }
    }

    setStatus(status) {
        this.status = status;
    }

    isSameFile(fileInfo1, fileInfo2) {
        return fileInfo1.fileName === fileInfo2.fileName && fileInfo1.fileSize === fileInfo2.fileSize
    }

    setDataSetId(dataSetId) {
        this.dataSetId = dataSetId;
    }

    setCurrFileInfo(fileInfo) {
        this.currFileInfo = fileInfo;
    }
}

export default RvtUvUploader;